import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';
import { API } from './_helpers';

export default function SwapProposeModal({ myShift, allShifts, sites, onClose, onCreated }) {
  const [colleagues, setColleagues] = useState([]);
  const [loadingC, setLoadingC] = useState(true);
  const [kind, setKind] = useState('give_away');   // 'give_away' | 'swap'
  const [targetUserId, setTargetUserId] = useState('');
  const [targetShiftId, setTargetShiftId] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const siteById = useMemo(() => Object.fromEntries((sites||[]).map(s => [s.site_id, s])), [sites]);

  useEffect(() => {
    // Build colleague list from visible published shifts (we don't have /users access for staff)
    const map = new Map();
    allShifts.forEach(s => {
      if (s.user_id && s.user_id !== myShift.user_id && s.status === 'published') {
        map.set(s.user_id, { id: s.user_id, name: s.user_name });
      }
    });
    setColleagues(Array.from(map.values()).sort((a,b) => (a.name||'').localeCompare(b.name||'')));
    setLoadingC(false);
  }, [allShifts, myShift.user_id]);

  // For kind=swap: show only target user's future published shifts
  const targetShifts = useMemo(() => {
    if (!targetUserId || kind !== 'swap') return [];
    const today = new Date().toISOString().slice(0,10);
    return allShifts.filter(s => s.user_id === targetUserId && s.status === 'published' && s.date >= today
                              && s.shift_id !== myShift.shift_id);
  }, [allShifts, targetUserId, kind, myShift.shift_id]);

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/roster/swaps`, {
        requester_shift_id: myShift.shift_id,
        target_user_id: targetUserId,
        kind,
        target_shift_id: kind === 'swap' ? targetShiftId : null,
        message: message || null,
      }, { withCredentials: true });
      onCreated();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not propose'); setBusy(false);
    }
  };

  const site = myShift.site_id ? siteById[myShift.site_id] : null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-xl" onClick={e => e.stopPropagation()}
           data-testid="swap-propose-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-bold" style={{ color: '#0d2644' }}>Give away or swap shift</h3>
            <p className="text-xs text-muted-foreground mt-0.5">A colleague will get an email with accept/decline links.</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-sm">
          <div className="text-[10px] text-green-700 font-bold uppercase tracking-wider">Your shift</div>
          <div className="font-semibold mt-0.5">
            {new Date(myShift.date+'T00:00:00').toLocaleDateString('en-AU', { weekday:'short', day:'numeric', month:'short' })}
            {' · '}{myShift.start}–{myShift.end}
            {myShift.role_label && <span> · {myShift.role_label}</span>}
            {site && <span> · {site.name}</span>}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2 rounded-lg border p-1">
            <button onClick={() => setKind('give_away')} data-testid="swap-kind-giveaway"
              className={`flex-1 text-xs font-semibold py-1.5 rounded-md ${kind==='give_away' ? 'bg-[#0d2644] text-white' : 'text-muted-foreground'}`}>
              Give away
            </button>
            <button onClick={() => setKind('swap')} data-testid="swap-kind-swap"
              className={`flex-1 text-xs font-semibold py-1.5 rounded-md ${kind==='swap' ? 'bg-[#0d2644] text-white' : 'text-muted-foreground'}`}>
              Direct swap
            </button>
          </div>

          <label className="block text-xs font-semibold text-muted-foreground">Send to
            <select value={targetUserId} onChange={e => { setTargetUserId(e.target.value); setTargetShiftId(''); }} data-testid="swap-target-user"
              className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-white">
              <option value="">— Choose colleague —</option>
              {loadingC && <option disabled>Loading…</option>}
              {colleagues.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <span className="text-[10px] text-muted-foreground mt-0.5 block">Only colleagues with published shifts this week appear here.</span>
          </label>

          {kind === 'swap' && targetUserId && (
            <label className="block text-xs font-semibold text-muted-foreground">In exchange for their shift
              <select value={targetShiftId} onChange={e => setTargetShiftId(e.target.value)} data-testid="swap-target-shift"
                className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-white">
                <option value="">— Choose their shift —</option>
                {targetShifts.map(s => (
                  <option key={s.shift_id} value={s.shift_id}>
                    {new Date(s.date+'T00:00:00').toLocaleDateString('en-AU', { weekday:'short', day:'numeric', month:'short' })}
                    {' · '}{s.start}–{s.end}{s.role_label?` · ${s.role_label}`:''}
                  </option>
                ))}
              </select>
              {targetShifts.length === 0 && <span className="text-[10px] text-red-600 block mt-0.5">No upcoming published shifts found for that colleague this week. Try Give-away instead.</span>}
            </label>
          )}

          <Textarea value={message} onChange={e => setMessage(e.target.value)} rows={2}
                    placeholder="Optional message (e.g. 'Got a family event — really appreciate it!')" />
          {err && <div className="text-xs text-red-600">{err}</div>}
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="text-sm font-semibold px-4 py-2 rounded-lg border">Cancel</button>
          <button onClick={submit} disabled={busy || !targetUserId || (kind==='swap' && !targetShiftId)} data-testid="swap-propose-save"
            className="text-sm font-semibold px-4 py-2 rounded-lg bg-[#7c3aed] text-white disabled:opacity-50">
            {busy ? 'Sending…' : 'Send proposal'}
          </button>
        </div>
      </div>
    </div>
  );
}
